import { MediaTypeCodeEnum } from '@minecraft.graphql-operations';
import { useCallback, useMemo } from 'react';
import { useTranslation } from '@blocs.i18n';
import { useUploadWidget, UseUploadWidgetParams } from './useUploadWidget';
import { handleFilesUpload } from './helpers';

export type HeadlessMediaUploaderParams = {
  widgetParams: Pick<UseUploadWidgetParams, 'maxFiles' | 'mediaType'>;
};

export const useHeadlessMediaUpload = ({ widgetParams = {} }: HeadlessMediaUploaderParams) => {
  const { t } = useTranslation();
  const widgetParamsWithDefaults = {
    maxFiles: 1,
    mediaType: MediaTypeCodeEnum.Photo,
    ...widgetParams,
  };

  const { initializeCloudinaryWidgetSilently } = useUploadWidget({
    ...widgetParamsWithDefaults,
  });

  const onFilesSelected = useCallback(
    async (files: File[]) => {
      const result = await handleFilesUpload({
        eventFiles: files,
        maxFiles: widgetParamsWithDefaults.maxFiles,
        currentMediaType: widgetParamsWithDefaults.mediaType,
        t,
      });

      if (result?.error) {
        throw new Error(result.error);
      }
      const { completedUpload } = initializeCloudinaryWidgetSilently(
        result.filesToPreview.map((f) => ({
          originalFile: f.originalFile,
          width: f.width,
          height: f.height,
        }))
      );

      return completedUpload();
    },
    [initializeCloudinaryWidgetSilently, t, widgetParamsWithDefaults.maxFiles, widgetParamsWithDefaults.mediaType]
  );

  return useMemo(
    () => ({
      onFilesSelected,
    }),
    [onFilesSelected]
  );
};
